import { cn } from '@/lib/cn';
import { Dialog, DialogContent, DialogTitle } from '@heidi/design-system/dialog';
import { Maximize2 } from 'lucide-react';
import { lazy, Suspense, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

// Lazy so hosts can import the expand affordance eagerly without dragging
// the CUA cloud client (auth/http chain) into their module graph — the
// login clarification card deliberately keeps that chain out until a live
// view actually renders.
const CloudComputerLiveView = lazy(() =>
  import('./CloudComputerLiveView').then((m) => ({
    default: m.CloudComputerLiveView,
  }))
);

/**
 * Corner affordance that opens the semi-fullscreen live view. Rendered as an
 * absolute overlay in the top-right of a live-view surface, matching the
 * usual "maximize" placement in video players. ``stopPropagation`` so hosts
 * whose whole surface is a button (e.g. the login card's take-over overlay)
 * don't also fire.
 */
export const CloudComputerExpandButton = ({
  onClick,
  className,
}: {
  onClick: () => void;
  className?: string;
}): JSX.Element => {
  const intl = useIntl();
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      aria-label={intl.formatMessage({
        defaultMessage: 'Expand live view',
        description: 'Maximize button on the cloud resident live view',
        id: 'qJ4a9M3U8/',
      })}
      data-testid="cloud-computer-expand-button"
      className={cn(
        'absolute right-2 top-2 z-10 rounded-md bg-black/60 p-1.5 text-white/80',
        'transition-colors hover:bg-black/80 hover:text-white',
        className
      )}
    >
      <Maximize2 className="size-3.5" aria-hidden />
    </button>
  );
};

/**
 * Semi-fullscreen presentation of the cloud resident's screen: a large
 * dialog (most of the viewport, dark backdrop, standard close X) hosting its
 * own ``CloudComputerLiveView`` — the VNC link is minted per mount, so the
 * lightbox opens a fresh session rather than re-parenting the inline iframe.
 *
 * ``interactive`` mirrors the host surface's rules: the watch-only inline
 * card opens a watch-only lightbox; the login card passes its take-over
 * state so the user can sign in at full size. ``footer`` lets the host
 * overlay its own controls (e.g. Take over / Hand back) inside the dialog.
 */
export const CloudComputerLightbox = ({
  open,
  onOpenChange,
  interactive,
  footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  interactive: boolean;
  footer?: ReactNode;
}): JSX.Element => {
  const intl = useIntl();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[min(94vw,1440px)] max-w-none gap-0 overflow-hidden border-0 bg-black p-0 sm:rounded-xl"
        overlayClassName="bg-black/80"
        closeIconClassName="bg-black/60 text-white/80 hover:text-white"
        data-testid="cloud-computer-lightbox"
      >
        <DialogTitle className="sr-only">
          {intl.formatMessage({
            defaultMessage: 'Your cloud resident',
            description: 'Accessible title of the expanded live view dialog',
            id: 'VVwu6BhfPK',
          })}
        </DialogTitle>
        <div className="relative">
          <Suspense
            fallback={<div className="aspect-video max-h-[85vh] w-full bg-black" />}
          >
            <CloudComputerLiveView
              className={cn(
                'aspect-video max-h-[85vh] w-full',
                !interactive && 'pointer-events-none'
              )}
            />
          </Suspense>
          {footer}
        </div>
      </DialogContent>
    </Dialog>
  );
};
